import type {RefObject} from 'react'
import {useEffect, useState, useCallback} from 'react'

type RefType<Element extends HTMLElement> =
  | RefObject<Element | null>
  | Element
  | null
type Callback = (e: PointerEvent) => void

interface ClickOutsideBaseProps {
  isOpen?: boolean
  deferInitialization?: boolean
}

interface ClickOutsideOneProps<Element extends HTMLElement>
  extends ClickOutsideBaseProps {
  ref: RefType<Element>
  callback: Callback
}

interface ClickOutsideTwoProps<
  Element extends HTMLElement,
  ElementTwo extends HTMLElement,
> extends ClickOutsideBaseProps {
  ref: RefType<Element>
  extraRef: RefType<ElementTwo>
  callback: Callback
}

type ClickOutsideProps<
  Element extends HTMLElement,
  ElementTwo extends HTMLElement,
> = ClickOutsideOneProps<Element> | ClickOutsideTwoProps<Element, ElementTwo>

function resolveElement<Element extends HTMLElement>(
  ref: RefType<Element>
): Element | null {
  if (ref === null) return null
  if (typeof ref === 'function') {
    throw new Error('not supported')
  }
  if ('current' in ref) return ref.current
  return ref
}

function checkIfClickIsOutside<Element extends HTMLElement>(
  event: PointerEvent,
  ref: RefType<Element>
) {
  let clickOutside = false
  const element = resolveElement(ref)

  if (!element) return false
  if (!event.target) return false

  const target = event.target
  if (!(target instanceof Node)) return false

  if ('contains' in element) {
    if (!element.contains(target)) {
      clickOutside = true
    }
  }

  return clickOutside
}

export function useClickOutside<
  Element extends HTMLElement,
  ElementTwo extends HTMLElement,
>({
  callback,
  isOpen,
  deferInitialization,
  ...props
}: ClickOutsideProps<Element, ElementTwo>) {
  const mainRef = props.ref
  const auxRef = 'extraRef' in props ? props.extraRef : undefined

  const [initialized, setInitialized] = useState(!deferInitialization)

  const handleClick = useCallback(
    (event: PointerEvent) => {
      if (!initialized) {
        setInitialized(true)
        return
      }

      if (!isOpen) return

      const clickOutside = auxRef
        ? checkIfClickIsOutside(event, mainRef) &&
          checkIfClickIsOutside(event, auxRef)
        : checkIfClickIsOutside(event, mainRef)

      if (clickOutside) {
        callback(event)
      }
    },
    [isOpen, mainRef, auxRef, callback, initialized]
  )

  useEffect(() => {
    document.addEventListener('click', handleClick, false)
    return () => document.removeEventListener('click', handleClick)
  }, [handleClick])
}
